import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type {
  ApiClient, Bestueckungszusammenfassung, Erkennungszusammenfassung, Laengenzusammenfassung, Linienzusammenfassung,
  Projekt, SvZuordnungszusammenfassung, Zuordnungszusammenfassung,
} from '../api';
import {
  IconOk, IconWarnung,
  IconRingmodul, IconNetzwerkmodul, IconNetzteil, IconAkku,
  IconBedienfeld, IconAnsteuerung, IconGehaeuse, IconBaugruppe,
} from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ConfirmDialog, ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, LoadingState, StatusBadge, Vorbehalte,
  formatiereZahl, type ExportZiel, type Spalte, type Vorbehalt,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

type Zentralenstand = Awaited<ReturnType<ApiClient['elektroZentralen']>>;
type Linienstand = Awaited<ReturnType<ApiClient['elektroLinien']>>;
type Zentrale = Zentralenstand['zentralen'][number];
type Linie = Linienstand['linien'][number];
type Meldergruppe = Linienstand['meldergruppen'][number];

/** Der Hinweis am Loopplan-Knopf (BM-5, F11): eine neue Fassung nur bei geändertem Stand. */
export const LOOPPLAN_HINWEIS =
  'Loopplan ELT-LOP mit Dokumentnummer aus dem Register: Bei geändertem Stand entsteht eine neue Fassung, sonst '
  + 'liefert der Abruf die abgelegte (F11).';

/** Die Vorbehalte der Seite (S1.2) - aus dem Stand, ohne eigene Rechnung. */
export function meldelinienVorbehalte(ohneZentrale: number): Vorbehalt[] {
  const v: Vorbehalt[] = [{
    art: 'annahmen',
    text: 'Die Bereiche der Linien und die Brandabschnitte der Meldergruppen sind aus Quadrant und Geschoss '
      + 'abgeleitet – Behelfsannahme, Ansatz des Eigentümers vom 20.10.2026 (BMA-02).',
  }];
  if (ohneZentrale > 0) {
    v.push({
      art: 'sonstiges',
      text: `${formatiereZahl(ohneZentrale)} Bauteil(e) ohne Zentrale – sie fehlen in Linien, Loopplänen und Melderliste.`,
    });
  }
  return v;
}

/** Die Linienarten in der Rückfrage, in dieser Reihenfolge (SA-4d). */
const ARTEN: [string, string][] = [
  ['bma_ring', 'Ringe'], ['rwa_linie', 'RWA-Linien'], ['saa_linie', 'SAA-Linien'],
  ['bma_stich', 'Stichleitungen'], ['notruf_linie', 'Notruflinien'],
];

function jeArt(r: Record<string, number>, immer: boolean): string {
  const bekannt = ARTEN
    .filter(([art], i) => immer && i < 3 ? true : (r[art] ?? 0) > 0)
    .map(([art, name]) => `${formatiereZahl(r[art] ?? 0)} ${name}`);
  const weitere = Object.keys(r).filter((a) => !ARTEN.some(([art]) => art === a) && r[a] > 0)
    .map((a) => `${formatiereZahl(r[a])} ${a}`);
  return [...bekannt, ...weitere].join(', ') || 'keine';
}

/**
 * SA-4d (main 21.10.2026): die Rückfrage vor dem Schreiben - aus der Probe der API, ohne eigene Rechnung. Nennt die
 * neu gebildeten Ringe, RWA- und SAA-Linien, die Meldergruppen, die stehenden Linien mit Anzahl und Grund, die
 * umgehängten Teilnehmer und dass gespeicherte Meldernummern danach nicht mehr passen.
 */
export function linienRueckfrage(z: Linienzusammenfassung): string[] {
  const zeilen = [
    `Neu gebildet: ${jeArt(z.linienNeu, true)}. Bisher ${jeArt(z.linienBisher, true)} – sie werden gelöscht.`,
    `Meldergruppen: ${formatiereZahl(z.gruppenNeu)} neu, ${formatiereZahl(z.gruppenBisher)} bisherige werden `
      + `gelöscht, ${formatiereZahl(z.gruppenStehend)} bleiben stehen.`,
  ];

  const gruende = [...new Set(z.stehend.map((s) => s.grund))];
  if (gruende.length === 0) zeilen.push('Es bleibt keine Linie stehen – alle abgeleiteten Linien werden neu gebildet.');
  for (const grund of gruende) {
    const s = z.stehend.filter((x) => x.grund === grund);
    zeilen.push(`Nicht neu gebildet: ${formatiereZahl(s.length)} Linie(n), Grund „${grund}“ – `
      + s.map((x) => `${x.kennzeichen} (${formatiereZahl(x.teilnehmer)} Teilnehmer`
        + (x.handTeilnehmer > 0 ? `, davon ${formatiereZahl(x.handTeilnehmer)} mit Hand-Zuordnung` : '') + ')').join(', ')
      + '. Ihre Teilnehmer, Adressen und Meldergruppen bleiben.');
  }

  zeilen.push(`Umgehängt: ${formatiereZahl(z.umgehaengt)} Teilnehmer bekommen eine andere Linie oder Adresse.`);
  zeilen.push(z.meldernummernBetroffen > 0
    ? `Meldernummern: ${formatiereZahl(z.meldernummernBetroffen)} der neu gebildeten Teilnehmer tragen eine `
      + 'gespeicherte Meldernummer – sie passt danach nicht mehr zu Linie, Adresse und Gruppe. Neu vergeben wird '
      + 'sie nicht automatisch.'
    : 'Meldernummern: Keiner der neu gebildeten Teilnehmer trägt eine gespeicherte Meldernummer.');
  if (z.handOhneLinie > 0) {
    zeilen.push(`${formatiereZahl(z.handOhneLinie)} Teilnehmer mit Hand-Zuordnung ohne Linie bleiben unzugeordnet.`);
  }
  return zeilen;
}

/** SA-4d: die Rückfrage vor dem Schreiben der Trassenlängen. */
export function laengenRueckfrage(z: Laengenzusammenfassung): string[] {
  return [
    `Gemessen: ${jeArt(z.jeArt, true)}.`,
    `${formatiereZahl(z.geaendert)} Linie(n) bekommen eine andere Länge – auch Ringe und RWA-Linien.`,
  ];
}

/**
 * SA-4e (main 21.10.2026): die Rückfrage vor „Zentralen zuordnen". Nennt ausdrücklich die Teilnehmer, die die Zentrale
 * wechseln, während ihre Linie an der alten bleibt - mit dem Satz „Danach Linien neu bilden" -, dazu die neu
 * zugeordneten, die unveränderten (nicht geschrieben), die von Hand zugeordneten und die offenen.
 */
export function zuordnungRueckfrage(z: Zuordnungszusammenfassung): string[] {
  const n = formatiereZahl;
  const zeilen = [
    z.linieBleibt > 0
      ? `Zentrale wechseln: ${n(z.wechsel)} Teilnehmer – davon hängen ${n(z.linieBleibt)} an einer Linie der alten `
        + 'Zentrale; ihre Linie bleibt dort, bis die Linien neu gebildet werden. Danach Linien neu bilden.'
      : `Zentrale wechseln: ${n(z.wechsel)} Teilnehmer – keiner hängt an einer Linie der alten Zentrale.`,
    ...z.jeWechsel.map((w) => `${w.von} → ${w.nach}: ${n(w.anzahl)} Teilnehmer`
      + (w.linieBleibt > 0 ? `, davon ${n(w.linieBleibt)} mit Linie an ${w.von}` : '')),
    `Neu zugeordnet: ${n(z.neu)} Teilnehmer ohne Zentrale.`,
    `Unverändert: ${n(z.unveraendert)} Teilnehmer – sie werden nicht geschrieben.`,
    `Von Hand zugeordnet und übersprungen: ${n(z.hand)}. Ohne passende Zentrale: ${n(z.offen)}.`,
  ];
  if (z.versorgungsbereiche > 0) zeilen.push(`Versorgungsbereiche neu gesetzt: ${n(z.versorgungsbereiche)} Zentrale(n).`);
  return zeilen;
}

/** SA-4f: die Herkunft einer geschützten Zuordnung oder Menge, wie die Rückfrage sie nennt. */
export function herkunftText(herkunft: string): string {
  return herkunft === 'hand' ? 'von Hand gepflegt' : 'Bestand, Herkunft unbekannt';
}

/**
 * SA-4f (main 21.10.2026): die Rückfrage vor „SV zuordnen". Nennt die neu zugeordneten SV-Verteiler, die Wechsel der
 * Anlage, die unveränderten (nicht geschrieben) und ausdrücklich die geschützten je Anlage mit ihrer Herkunft – sie
 * bleiben, auch wenn das Verfahren sie anders verteilen würde.
 */
export function svRueckfrage(z: SvZuordnungszusammenfassung): string[] {
  const n = formatiereZahl;
  return [
    `Neu zugeordnet: ${n(z.neu)} SV-Verteiler ohne Anlage.`,
    `Anlage wechseln: ${n(z.wechsel)} SV-Verteiler`
      + (z.wechsel > 0 ? ' – ihre Endstromkreise zählen danach bei der neuen Anlage.' : '.'),
    ...z.jeWechsel.map((w) => `${w.von} → ${w.nach}: ${n(w.anzahl)} SV-Verteiler`),
    `Unverändert: ${n(z.unveraendert)} SV-Verteiler – sie werden nicht geschrieben.`,
    z.geschuetzt > 0
      ? `Geschützt, bleiben unverändert: ${n(z.geschuetzt)} SV-Verteiler – `
        + z.jeGeschuetzt.map((g) => `${g.anlage}: ${n(g.anzahl)} (${herkunftText(g.herkunft)}, `
          + `${n(g.endstromkreise)} Endstromkreise)`).join(', ') + '.'
      : 'Geschützt: keine Zuordnung.',
    `Ohne passende Anlage: ${n(z.offen)}.`,
  ];
}

/**
 * SA-4f (main 21.10.2026): die Rückfrage vor „Bestückung bilden". Nennt die neu angelegten Baugruppen, die angepassten
 * berechneten Mengen (vorher → nachher) und ausdrücklich die geschützten mit gepflegter und gerechneter Menge.
 */
export function bestueckungRueckfrage(z: Bestueckungszusammenfassung): string[] {
  const n = formatiereZahl;
  return [
    `Neu angelegt: ${n(z.neu)}`
      + (z.jeNeu.length > 0 ? ' – ' + z.jeNeu.map((x) => `${n(x.menge)}× ${x.bezeichnung}`).join(', ') : '') + '.',
    `Menge angepasst: ${n(z.angepasst)}`
      + (z.jeAngepasst.length > 0
        ? ' – ' + z.jeAngepasst.map((x) => `${x.bezeichnung} ${n(x.vorher)}× → ${n(x.nachher)}×`).join(', ') : '') + '.',
    `Unverändert: ${n(z.unveraendert)}.`,
    z.geschuetzt > 0
      ? `Geschützt, nicht angepasst: ` + z.jeGeschuetzt.map((x) => `${x.bezeichnung} ${n(x.gepflegt)}× `
        + `(${herkunftText(x.herkunft)}), gerechnet ${n(x.gerechnet)}×`).join(', ') + '.'
      : 'Geschützt: keine Menge.',
  ];
}

/**
 * SA-4e Teil 2 (main 21.10.2026, Z-b): die Rückfrage von „Zentralen erkennen" nennt ausdrücklich jede Zentrale, die neu
 * angelegt würde - auch eine, die jemand von Hand entfernt hat, käme so wieder; der Anwender entscheidet.
 */
export function erkennenRueckfrage(
  z: Erkennungszusammenfassung,
  neu: { kennzeichen: string; bezeichnung: string | null; raumnummer: string | null; felder: number }[],
  fehlend: string[],
): string[] {
  const n = formatiereZahl;
  return [
    `Erkannt: ${n(z.erkannt)} Zentrale(n), davon ${n(z.schonAngelegt)} schon angelegt – sie bleiben unverändert.`,
    `Neu angelegt würden ${n(z.anzulegen)}:`,
    ...neu.map((g) => `${g.kennzeichen} · ${g.bezeichnung ?? '—'} · Raum ${g.raumnummer ?? '—'}`
      + ` · ${n(g.felder)} Feld${g.felder > 1 ? 'er' : ''}`),
    ...(z.zusammengefasst > 0
      ? [`Im selben Raum wie eine neue: ${n(z.zusammengefasst)} – keine eigene Zentrale.`] : []),
    'Eine von Hand entfernte Zentrale stünde hier wieder – dann abbrechen.',
    ...fehlend,
  ];
}

type SvWeg = 'gleichmaessig' | 'kapazitaet' | 'naehe' | 'hand';

type Rueckfrage =
  | { art: 'bilden'; weg: 'geschossweise' | 'dicht'; token: string; zeilen: string[] }
  | { art: 'laengen'; token: string; zeilen: string[] }
  | { art: 'zuordnen'; weg: 'hand' | 'quadrant' | 'naehe'; token: string; zeilen: string[] }
  | { art: 'sv'; weg: SvWeg; token: string; zeilen: string[] }
  | { art: 'erkennen'; token: string; zeilen: string[] };

/** Die Kennwerte einer SV-Anlage als eine Zeile - ohne toFixed (S1.7). */
export function svKennwerte(z: Pick<Zentrale, 'svArt' | 'betriebsdauer' | 'umschaltzeit'>): string {
  return [
    z.svArt,
    z.betriebsdauer != null ? `${formatiereZahl(z.betriebsdauer)} min` : null,
    z.umschaltzeit != null ? `${formatiereZahl(z.umschaltzeit * 1000, 0)} ms` : null,
  ].filter(Boolean).join(' · ') || 'Kennwerte offen';
}

/**
 * Meldelinien: Zentralen, Ringleitungen und Meldergruppen.
 *
 * --- Warum das nicht bei den Verteilern steht ------------------------------------
 *
 * Ein Rauchmelder bekommt seinen Strom von der Brandmeldezentrale, nicht von der
 * Unterverteilung des Raums, in dem er hängt. 4.158 Bauteile waren dort falsch
 * zugeordnet, weil ihr Raum ein `elt_uv` trug — und in der Baumansicht sah es
 * richtig aus.
 *
 * Die einzige Verbindung zwischen beiden Welten ist die Einspeisung DER ZENTRALE.
 *
 * --- Vier Topologien ------------------------------------------------------------
 *
 *   bma_ring     adressierbarer Datenbus, 127 Teilnehmer, Ring
 *   saa_linie    100-V-Leistungsleitung, Grenze ist die Verstärkerleistung
 *   rwa_linie    konventionelle Linie, E90
 *   notruf_linie eigene Zentrale nach DIN VDE 0834
 *
 * Einen Lautsprecher auf einen Loop zu legen wäre fachlich falsch: Ein Loop ist ein
 * Datenbus mit Adressen, eine ELA-Linie eine Leistungsleitung.
 *
 * --- Seit BM-5 nach TX-STANDARD S1 -------------------------------------------------
 *
 * Seitenkopf mit Bestand und Ausgaben (Melderliste ELT-MLD, BMA/RWA-Listen), Vorbehalte, drei DataTable statt
 * roher Tabellen, Zahlen über formatiereZahl. Der Loopplan je Zentrale ist die registrierte Fassung ELT-LOP.
 */
export function ElektroMeldelinien() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <MeldelinienAnsicht api={api} projektId={projekt.id} />;
}

export function MeldelinienAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<Zentralenstand | null>(null);
  const [linien, setLinien] = useState<Linienstand | null>(null);

  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [probe, setProbe] = useState<string[] | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [rueckfrage, setRueckfrage] = useState<Rueckfrage | null>(null);

  const laden = async () => {
    try {
      const [z, l] = await Promise.all([
        api.elektroZentralen(projektId),
        api.elektroLinien(projektId),
      ]);

      setStand(z);
      setLinien(l);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { void laden(); }, [projektId]);

  /**
   * Einen Lauf ausführen und die Meldung zeigen.
   *
   * `T extends { message: string }` und `void` ausdrücklich in der Signatur: Die
   * Client-Aufrufe geben `T | void` zurück, weil `schicke` bei einem leeren Körper
   * nichts liefert. Ohne das `void` engt die Prüfung `if (!a)` den Typ nicht ein, und
   * TypeScript meldet für jedes Feld, dass es auf `void` nicht existiert.
   */
  const lauf = async <T extends { message: string }>(
    was: () => Promise<T | null | void>,
    zeilen?: (a: T) => string[],
  ) => {
    setLaeuft(true);
    setProbe(null);

    try {
      const a = await was();

      if (!a) { setMeldung('Keine Antwort von der Schnittstelle.'); return; }

      if (zeilen) setProbe([a.message, ...zeilen(a)]);
      else setMeldung(a.message);

      await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  // SA-4d: erst die Probe, dann die Rückfrage mit ihren Zahlen, geschrieben wird nur mit dem Token der Probe.
  const bildenPruefen = (weg: 'geschossweise' | 'dicht') => lauf(async () => {
    const a = await api.elektroLinienBilden(projektId, weg, true);
    if (a && a.token && a.zusammenfassung) {
      setRueckfrage({ art: 'bilden', weg, token: a.token, zeilen: linienRueckfrage(a.zusammenfassung) });
    }
    return a;
  }, (a) => a.hinweise);

  const laengenPruefen = () => lauf(async () => {
    const a = await api.elektroLinienLaengen(projektId, true);
    if (a && a.token && a.zusammenfassung) {
      setRueckfrage({ art: 'laengen', token: a.token, zeilen: laengenRueckfrage(a.zusammenfassung) });
    }
    return a;
  }, (a) => a.hinweise);

  const zuordnenPruefen = (weg: 'hand' | 'quadrant' | 'naehe') => lauf(async () => {
    const a = await api.elektroZentralenZuordnen(projektId, weg, true);
    if (a && a.token && a.zusammenfassung) {
      setRueckfrage({ art: 'zuordnen', weg, token: a.token, zeilen: zuordnungRueckfrage(a.zusammenfassung) });
    }
    return a;
  }, (a) => [...a.vorgeschlagen, ...a.hinweise]);

  // SA-4f: SV zuordnen ebenso - die Rückfrage nur, wenn die Probe etwas zu schreiben hat.
  const svStand = (a: { stand: { kennzeichen: string; verteiler: number; endstromkreise: number;
    grenze: number | null; module: number | null }[]; hinweise: string[] }) => [
    ...a.stand.map((x) =>
      `${x.kennzeichen}: ${x.verteiler} Verteiler, `
      + `${x.endstromkreise} von ${x.grenze ?? '?'} Endstromkreisen, `
      + `${x.module ?? '?'} Module`),
    ...a.hinweise,
  ];

  const svPruefen = (weg: SvWeg) => lauf(async () => {
    const a = await api.elektroSvZuordnen(projektId, weg, true);
    if (a && a.token && a.zusammenfassung) {
      const z = a.zusammenfassung;
      if (z.neu + z.wechsel > 0) setRueckfrage({ art: 'sv', weg, token: a.token, zeilen: svRueckfrage(z) });
      else return { ...a, hinweise: ['Es gibt nichts zu schreiben.', ...svRueckfrage(z), ...a.hinweise] };
    }
    return a;
  }, svStand);

  // SA-4e Teil 2: Zentralen erkennen ebenso - erst die Probe, die Rückfrage nur, wenn etwas anzulegen ist.
  const erkannteZeilen = (a: { gefunden: { kennzeichen: string; bezeichnung: string | null; raumnummer: string | null;
    felder: number }[]; fehlend: string[] }) => [
    ...a.gefunden.map((g) => `${g.kennzeichen} · ${g.bezeichnung ?? '—'} · ${g.raumnummer ?? '—'}`
      + ` · ${g.felder} Feld${g.felder > 1 ? 'er' : ''}`),
    ...a.fehlend,
  ];

  const erkennenPruefen = () => lauf(async () => {
    const a = await api.elektroZentralenErkennen(projektId, true);
    if (a && a.token && a.zusammenfassung) {
      const z = a.zusammenfassung;
      if (z.anzulegen > 0) {
        setRueckfrage({ art: 'erkennen', token: a.token, zeilen: erkennenRueckfrage(z, a.gefunden, a.fehlend) });
      } else {
        return { ...a, gefunden: [], fehlend: ['Es gibt nichts anzulegen.', ...a.fehlend] };
      }
    }
    return a;
  }, erkannteZeilen);

  const schreiben = () => {
    const r = rueckfrage;
    setRueckfrage(null);
    if (!r) return;
    if (r.art === 'erkennen') {
      void lauf(() => api.elektroZentralenErkennen(projektId, false, r.token), erkannteZeilen);
    } else if (r.art === 'bilden') {
      void lauf(() => api.elektroLinienBilden(projektId, r.weg, false, r.token), (a) => a.hinweise);
    } else if (r.art === 'laengen') {
      void lauf(() => api.elektroLinienLaengen(projektId, false, r.token), (a) => a.hinweise);
    } else if (r.art === 'sv') {
      void lauf(() => api.elektroSvZuordnen(projektId, r.weg, false, r.token), svStand);
    } else {
      void lauf(() => api.elektroZentralenZuordnen(projektId, r.weg, false, r.token),
        (a) => [...a.vorgeschlagen, ...a.hinweise]);
    }
  };

  const loopplan = async (z: Zentrale) => {
    setLaeuft(true);
    try {
      await api.elektroLoopplanLaden(z.id, z.kennzeichen);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const anzahlLinien = linien?.linien.length ?? 0;
  const zuordnen = stand?.ohneZentrale.reduce((s, x) => s + x.anzahl, 0) ?? 0;

  const ziele: ExportZiel[] = [
    {
      art: 'pdf', beschriftung: 'Melderliste ELT-MLD', laden: () => api.elektroMelderlisteLaden(projektId),
      hinweis: 'Alle Melder projektweit, je Zentrale und Linie – mit Dokumentnummer aus dem Register.',
      gesperrt: anzahlLinien === 0 ? { grund: 'Noch keine Linie gebildet.' } : undefined,
    },
    {
      art: 'excel', beschriftung: 'BMA/RWA-Listen', laden: () => api.elektroSicherheitstechnikListen(projektId),
      gesperrt: anzahlLinien === 0 ? { grund: 'Noch keine Linie gebildet.' } : undefined,
    },
  ];

  const meta = stand
    ? `${formatiereZahl(stand.zentralen.length)} Zentralen · ${formatiereZahl(anzahlLinien)} Linien · `
      + `${formatiereZahl(linien?.meldergruppen.length ?? 0)} Meldergruppen · Bestand beim Abruf`
    : 'noch nicht geladen';

  const offenZ = stand?.zentralen.find((z) => z.id === offen) ?? null;

  return (
    <div className="seite">
      <PageHeader
        titel="Zentralen, Meldergruppen und Ringe"
        beschreibung="Zentralen erkennen und zuordnen, Linien bilden, Bestückung pflegen – mit Loopplan je Zentrale (ELT-LOP), Melderliste ELT-MLD und den BMA/RWA-Listen."
        meta={meta}
        aktionen={<ExportLeiste ziele={ziele} />}
      />
      <ConfirmDialog
        offen={rueckfrage !== null}
        art="warnung"
        titel={rueckfrage?.art === 'laengen' ? 'Trassenlängen schreiben?'
          : rueckfrage?.art === 'zuordnen' ? 'Bauteile den Zentralen zuordnen?'
            : rueckfrage?.art === 'sv' ? 'SV-Verteiler den Anlagen zuordnen?'
              : rueckfrage?.art === 'erkennen' ? 'Zentralen anlegen?'
                : 'Linien und Meldergruppen neu bilden?'}
        text="Aus der Probe eben – geschrieben wird genau dieser Stand. Hat sich inzwischen etwas geändert, lehnt der Server ab."
        bestaetigenText={rueckfrage?.art === 'laengen' ? 'Ja, Längen schreiben'
          : rueckfrage?.art === 'zuordnen' ? 'Ja, zuordnen'
            : rueckfrage?.art === 'sv' ? 'Ja, SV zuordnen'
              : rueckfrage?.art === 'erkennen' ? 'Ja, anlegen' : 'Ja, neu bilden'}
        beiBestaetigen={schreiben}
        beiAbbrechen={() => setRueckfrage(null)}
        laeuft={laeuft}
      >
        <ul className="rueckfrage-zeilen">
          {rueckfrage?.zeilen.map((z) => <li key={z}>{z}</li>)}
        </ul>
      </ConfirmDialog>
      {fehler && (
        <ErrorState titel="Zentralen und Linien konnten nicht geladen werden." technisch={fehler}
                    beiErneutVersuchen={() => { void laden(); }} />
      )}
      {!fehler && !stand && <LoadingState />}
      {stand && (
        <>
          <Vorbehalte eintraege={meldelinienVorbehalte(zuordnen)} />

          {/* --- Der Ablauf ---------------------------------------------------- */}
          <ContentSection titel="Ablauf"
                          beschreibung={(
                            <>
                              <strong>von Hand</strong> nutzt den gepflegten Versorgungsbereich —{' '}
                              <strong>quadrant</strong> und <strong>nähe</strong> sind Ableitungen, keine
                              Planung. Und <strong>geschossweise</strong> hält jede Linie in einem
                              Brandabschnitt: 55 Ringe statt 39, aber 656 m statt 1.649 m im Mittel.
                            </>
                          )}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button" className="m-knopf"
                onClick={() => void erkennenPruefen()}
                disabled={laeuft}
                title="Aus der Bezeichnung im Modell, nach Raum gruppiert"
              >
                Zentralen erkennen
              </button>

              {/* Die drei Zuordnungswege. 'hand' ist der Standard: Er nutzt den
                  gepflegten Versorgungsbereich und ordnet nichts zu, was nicht
                  eindeutig ist. */}
              {(['hand', 'quadrant', 'naehe'] as const).map((weg) => (
                <button
                  key={weg} type="button"
                  onClick={() => { void zuordnenPruefen(weg); }}
                  disabled={laeuft}
                  className={weg === 'hand' ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
                  title={weg === 'hand'
                    ? 'Über den gepflegten Versorgungsbereich — ordnet nichts zu, was nicht eindeutig ist'
                    : weg === 'quadrant'
                      ? 'Schlägt Versorgungsbereiche aus der Lage vor und ordnet dann zu — eine Ableitung'
                      : 'Manhattan-Strecke zur nächsten Zentrale — die schwächste Begründung'}
                >
                  zuordnen: {weg === 'hand' ? 'von Hand' : `nach ${weg}`}
                </button>
              ))}

              {(['geschossweise', 'dicht'] as const).map((weg) => (
                <button
                  key={weg} type="button"
                  onClick={() => { void bildenPruefen(weg); }}
                  disabled={laeuft || stand.zentralen.length === 0}
                  className={weg === 'geschossweise' ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
                  title={weg === 'geschossweise'
                    ? 'Jede Linie bleibt in einem Quadranten und Geschoss — mehr Linien, aber kurze Wege'
                    : 'Bis zur Grenze füllen, auch über Geschossgrenzen — die wenigsten Linien, aber lange Wege'}
                >
                  Linien bilden: {weg}
                </button>
              ))}

              <button
                type="button" className="m-knopf"
                onClick={() => { void laengenPruefen(); }}
                disabled={laeuft || anzahlLinien === 0}
                title="Von der Zentrale durch alle Teilnehmer, beim Ring zurück — plus 20 % Zuschlag"
              >
                Trassenlängen ermitteln
              </button>
            </div>
          </ContentSection>

          {/* --- Die Sicherheitsstromversorgung -------------------------------- */}
          {stand.zentralen.some((z) => z.art === 'sv') && (
            <ContentSection titel="Sicherheitsstromversorgung"
                            beschreibung={(
                              <>
                                <strong>gleichmäßig</strong> teilt die Endstromkreise durch die Zahl der
                                Anlagen — bei 178 Kreisen auf drei Anlagen sind das 60 statt der
                                Schrankgrenze von 88, und es bleibt Reserve in jeder Anlage.{' '}
                                <strong>nach Kapazität</strong> füllt bis zur Grenze: 88 / 88 / 2 —
                                technisch richtig, aber jede Nachrüstung in den ersten beiden Bereichen
                                braucht dann einen neuen Schrank. Beide durchlaufen die Verteiler nach
                                Quadrant und Geschoss, damit jede Anlage einen{' '}
                                <strong>zusammenhängenden Bereich</strong> versorgt.
                              </>
                            )}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {(['gleichmaessig', 'kapazitaet', 'naehe', 'hand'] as const).map((weg) => (
                  <button
                    key={weg} type="button"
                    onClick={() => { void svPruefen(weg); }}
                    disabled={laeuft}
                    className={weg === 'gleichmaessig' ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
                    title={weg === 'gleichmaessig'
                      ? 'Die Endstromkreise durch die Zahl der Anlagen teilen — Reserve in JEDER Anlage'
                      : weg === 'kapazitaet'
                        ? 'Bis zur Schrankgrenze füllen — zwei Anlagen voll, eine fast leer'
                        : weg === 'naehe'
                          ? 'Manhattan-Strecke zur nächsten Anlage'
                          : 'Über den gepflegten Versorgungsbereich'}
                  >
                    SV zuordnen: {weg === 'gleichmaessig' ? 'gleichmäßig'
                      : weg === 'kapazitaet' ? 'nach Kapazität'
                        : weg === 'naehe' ? 'nach Nähe' : 'von Hand'}
                  </button>
                ))}
              </div>
            </ContentSection>
          )}

          {meldung && (
            <div className="hinweis" style={{ marginBottom: 12 }}>
              {meldung}
              <button type="button" className="m-knopf" onClick={() => setMeldung(null)} style={{ marginLeft: 8 }}>
                schließen
              </button>
            </div>
          )}

          {probe && (
            <div className="hinweis" style={{ marginBottom: 12 }}>
              <div style={{ whiteSpace: 'pre-line' }}>{probe.filter(Boolean).join('\n')}</div>
              <button type="button" className="m-knopf" onClick={() => setProbe(null)} style={{ marginTop: 8 }}>
                schließen
              </button>
            </div>
          )}

          <Zentralentabelle zeilen={stand.zentralen} laeuft={laeuft} offen={offen}
                            beiBestueckung={(z) => setOffen(offen === z.id ? null : z.id)}
                            beiLoopplan={(z) => { void loopplan(z); }}
                            beiPruefen={(z) => lauf(() => api.elektroZentralePruefen(z.id), (a) => a.befunde)} />

          {offenZ && (
            <ContentSection titel={`Bestückung ${offenZ.kennzeichen}`}
                            beschreibung="Baureihe und Baugruppen – daraus ergibt sich, wie viele Ringe die Zentrale trägt.">
              <Bestueckung api={api} zentraleId={offenZ.id}
                           fertig={(m) => { setMeldung(m); void laden(); }} />
            </ContentSection>
          )}

          {linien && <Linientabelle zeilen={linien.linien} />}
          {linien && <Gruppentabelle zeilen={linien.meldergruppen} />}
        </>
      )}
    </div>
  );
}

// === Die drei Tabellen (BM-5: DataTable statt roher Tabellen) ===============

function Zentralentabelle({ zeilen, laeuft, offen, beiBestueckung, beiLoopplan, beiPruefen }: {
  zeilen: Zentrale[]; laeuft: boolean; offen: string | null;
  beiBestueckung: (z: Zentrale) => void; beiLoopplan: (z: Zentrale) => void; beiPruefen: (z: Zentrale) => void;
}) {
  const spalten: Spalte<Zentrale>[] = [
    {
      schluessel: 'zentrale', titel: 'Zentrale', wert: (z) => z.kennzeichen, immerSichtbar: true,
      inhalt: (z) => (
        <>
          <strong>{z.kennzeichen}</strong>
          {z.felder != null && z.felder > 1 && <div className="sehr-schwach">{formatiereZahl(z.felder)} Felder</div>}
        </>
      ),
    },
    { schluessel: 'art', titel: 'Art', wert: (z) => z.art.toUpperCase() },
    {
      schluessel: 'baureihe', titel: 'Baureihe', wert: (z) => z.baureihe ?? z.typ ?? '',
      // Bei einer SV-Anlage sind Betriebsdauer und Umschaltzeit die Kennwerte, die über die Tauglichkeit
      // entscheiden — nicht die Baureihe.
      inhalt: (z) => (
        <>
          {z.baureihe ?? z.typ ?? '—'}
          {z.art === 'sv' && <div className="sehr-schwach">{svKennwerte(z)}</div>}
        </>
      ),
    },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raumnummer ?? '—' },
    {
      schluessel: 'versorgt', titel: 'Versorgt', art: 'status', wert: (z) => z.versorgteQuadranten ?? '',
      inhalt: (z) => (z.versorgteQuadranten
        ? `Q ${z.versorgteQuadranten}`
        : <StatusBadge zustand="warnung" text="offen" klein
                       titel="Ohne Versorgungsbereich ist die Zuordnung bei mehreren Zentralen nicht entscheidbar" />),
    },
    {
      schluessel: 'linien', titel: 'Linien', art: 'zahl', wert: (z) => (z.art === 'sv' ? null : z.linien),
      titelHinweis: 'Bei einer SV-Anlage sind es keine Linien, sondern die Endstromkreise der gespeisten Verteiler',
      inhalt: (z) => (
        <>
          {z.art === 'sv' ? '—' : formatiereZahl(z.linien)}
          {z.maxLinien != null && (
            <span className="sehr-schwach">
              {z.art === 'sv' ? ` ${formatiereZahl(z.maxLinien)} Mod` : ` / ${formatiereZahl(z.maxLinien)}`}
            </span>
          )}
        </>
      ),
    },
    { schluessel: 'teilnehmer', titel: 'Teilnehmer', art: 'zahl', wert: (z) => z.teilnehmer },
    {
      schluessel: 'aktionen', titel: 'Aktionen', wert: () => '', sortierbar: false, immerSichtbar: true,
      inhalt: (z) => {
        const ohneLinien = z.art !== 'sv' && z.linien === 0;
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" className="m-knopf" aria-pressed={offen === z.id}
                    onClick={() => beiBestueckung(z)}
                    title="Baureihe und Bestückung — daraus ergibt sich, wie viele Ringe die Zentrale trägt">
              Bestückung
            </button>
            <button type="button" className="m-knopf" disabled={laeuft || ohneLinien}
                    aria-label={`${z.art === 'sv' ? 'Anlagenplan' : 'Loopplan'} ${z.kennzeichen} als PDF`}
                    onClick={() => beiLoopplan(z)}
                    title={(z.art === 'sv'
                      ? 'Anlagenplan als A3-PDF: Übersicht, Bestückung, versorgte SV-Verteiler und ihre Endstromkreise. '
                      : 'Loopplansatz als A3-PDF: Übersicht, Ringdarstellung je Linie, Meldergruppenliste. ')
                      + LOOPPLAN_HINWEIS}>
              {z.art === 'sv' ? 'Anlagenplan' : 'Loopplan'}
            </button>
            <button type="button" className="m-knopf" disabled={laeuft || ohneLinien}
                    onClick={() => beiPruefen(z)}
                    title={z.art === 'sv'
                      ? 'Bestückung, Betriebsdauer, Umschaltzeit und die Endstromkreise der versorgten Verteiler'
                      : 'Bestückung, Baureihe, Ringadressen, Längen und Meldergruppen — ohne PDF'}>
              prüfen
            </button>
          </div>
        );
      },
    },
  ];
  return (
    <ContentSection titel="Zentralen">
      <DataTable name={`Zentralen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.id} suche={false} dicht
                 leer={(
                   <EmptyState titel="Keine Zentrale angelegt"
                               erklaerung="„Zentralen erkennen“ sucht sie im Modell anhand der Bezeichnung — eine BMZ steht aber oft in der Pförtnerloge und ist nicht modelliert. Dann von Hand anlegen." />
                 )} />
    </ContentSection>
  );
}

function Linientabelle({ zeilen }: { zeilen: Linie[] }) {
  const spalten: Spalte<Linie>[] = [
    { schluessel: 'linie', titel: 'Linie', wert: (l) => l.kennzeichen, immerSichtbar: true },
    { schluessel: 'art', titel: 'Art', wert: (l) => l.art },
    { schluessel: 'zentrale', titel: 'Zentrale', wert: (l) => l.zentrale ?? '—' },
    {
      schluessel: 'bereich', titel: 'Bereich', wert: (l) => (l.quadrant ? `Q${l.quadrant} ${l.geschoss ?? ''}`.trim() : 'mehrere'),
      inhalt: (l) => (l.quadrant
        ? `Q${l.quadrant} ${l.geschoss ?? ''}`.trim()
        : <span className="sehr-schwach" title={l.hinweis ?? ''}>mehrere</span>),
    },
    { schluessel: 'teilnehmer', titel: 'Teilnehmer', art: 'zahl', wert: (l) => l.teilnehmer },
    { schluessel: 'laenge', titel: 'Länge', einheit: 'm', art: 'zahl', nachkomma: 0, wert: (l) => l.laenge },
    {
      schluessel: 'leitung', titel: 'Leitung',
      wert: (l) => [l.leitungsart ?? '—', l.funktionserhalt].filter(Boolean).join(' '),
    },
  ];
  return (
    <ContentSection titel="Linien">
      <DataTable name={`Linien (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(l) => l.id} dicht hoehe={380}
                 leer={<EmptyState titel="Keine Linie gebildet" erklaerung="„Linien bilden“ legt sie aus den zugeordneten Bauteilen an." />} />
    </ContentSection>
  );
}

function Gruppentabelle({ zeilen }: { zeilen: Meldergruppe[] }) {
  const spalten: Spalte<Meldergruppe>[] = [
    { schluessel: 'gruppe', titel: 'Gruppe', wert: (g) => g.kennzeichen, immerSichtbar: true },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (g) => g.bezeichnung ?? '—' },
    { schluessel: 'melder', titel: 'Melder', art: 'zahl', wert: (g) => g.melder },
    {
      schluessel: 'linien', titel: 'Linien', art: 'zahl', wert: (g) => g.linien,
      inhalt: (g) => (
        <>
          {g.linien != null ? formatiereZahl(g.linien) : '—'}
          {(g.linien ?? 0) > 2 && (
            <StatusBadge zustand="warnung" text="über 2 Linien" klein
                         titel="Bei einem Ringbruch fällt ein Teil aus, während die Zentrale die Gruppe weiter als in Ordnung anzeigt" />
          )}
        </>
      ),
    },
    { schluessel: 'raeume', titel: 'Räume', wert: (g) => g.raeume ?? '—' },
  ];
  return (
    <ContentSection titel="Meldergruppen"
                    beschreibung={(
                      <>
                        Die Gruppe hängt an der <strong>Zentrale</strong>, nicht an der Linie: In einem adressierbaren
                        System definiert die Zentrale die Gruppen in ihrer Programmierung, und ein Melder auf Ring 3
                        kann in derselben Gruppe liegen wie einer auf Ring 7. Nach DIN 14675 sind Handfeuermelder von
                        automatischen Meldern getrennt — die Zentrale muss unterscheiden können, ob ein Mensch
                        gemeldet hat oder ein Sensor.
                      </>
                    )}>
      <DataTable name={`Meldergruppen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(g) => g.id} dicht hoehe={320}
                 leer={<EmptyState titel="Keine Meldergruppe" erklaerung="Meldergruppen entstehen mit den Linien." />} />
    </ContentSection>
  );
}

// === Die Bestückung einer Zentrale ==========================================

/**
 * Baureihe und Baugruppen.
 *
 * --- Warum die Ringzahl aus der Bestückung kommt --------------------------------
 *
 * Sie ist keine Eigenschaft der Zentrale. Eine ESSER FlexES Control FX18 trägt
 * höchstens achtzehn Ringleitungen — aber nur, wenn die Ringmodule stecken. Mit zwei
 * Modulen sind es zwei.
 *
 * `MaxLinien` war ein Feld, das jemand eintragen musste, und es blieb leer. Aus der
 * Bestückung folgt es zwingend.
 */
function Bestueckung({
  api, zentraleId, fertig,
}: {
  api: ApiClient;
  zentraleId: string;
  fertig: (m: string) => void;
}) {
  const [daten, setDaten] = useState<Awaited<
    ReturnType<ApiClient['elektroBaugruppen']>> | null>(null);

  const [reihen, setReihen] = useState<Awaited<
    ReturnType<ApiClient['elektroBaureihen']>> | null>(null);

  const [laeuft, setLaeuft] = useState(false);
  const [neu, setNeu] = useState({ art: 'ringmodul', bezeichnung: '',
                                   anzahl: '1', ringeJeStueck: '1' });
  const [rueckfrage, setRueckfrage] = useState<{ optional: boolean; token: string; zeilen: string[] } | null>(null);

  const laden = async () => {
    try {
      const [b, r] = await Promise.all([
        api.elektroBaugruppen(zentraleId),
        api.elektroBaureihen(),
      ]);

      setDaten(b);
      setReihen(r);
    } catch (e) {
      fertig(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { laden(); }, [zentraleId]);

  /**
   * SA-4f (main 21.10.2026): erst die Probe, dann die Rückfrage mit ihren Zahlen; geschrieben wird nur nach
   * Bestätigung mit dem Token der Probe. Hat die Probe nichts zu schreiben, gibt es keine Rückfrage.
   */
  const bilden = async (optional: boolean, token?: string) => {
    setLaeuft(true);
    try {
      const a = await api.elektroBestueckungBilden(zentraleId, !token, optional, token);
      const z = a?.zusammenfassung;
      if (!token && a && a.token && z && z.neu + z.angepasst > 0) {
        setRueckfrage({ optional, token: a.token, zeilen: bestueckungRueckfrage(z) });
        return;
      }

      fertig(a
        ? [a.message, ...(z && !token ? ['Es gibt nichts zu schreiben.', ...bestueckungRueckfrage(z)] : a.angelegt),
          ...a.hinweise].join('\n')
        : 'Gebildet.');

      await laden();
    } catch (e) {
      fertig(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  if (!daten) return <LoadingState />;

  const z = daten.zentrale;

  return (
    <div style={{ padding: '6px 0' }}>
      <ConfirmDialog
        offen={rueckfrage !== null}
        art="warnung"
        titel="Bestückung schreiben?"
        text="Aus der Probe eben – geschrieben wird genau dieser Stand. Hat sich inzwischen etwas geändert, lehnt der Server ab."
        bestaetigenText="Ja, Bestückung schreiben"
        beiBestaetigen={() => {
          const r = rueckfrage;
          setRueckfrage(null);
          if (r) void bilden(r.optional, r.token);
        }}
        beiAbbrechen={() => setRueckfrage(null)}
        laeuft={laeuft}
      >
        <ul className="rueckfrage-zeilen">
          {rueckfrage?.zeilen.map((z) => <li key={z}>{z}</li>)}
        </ul>
      </ConfirmDialog>
      {/* --- Die Baureihe ---------------------------------------------- */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end',
                    marginBottom: 8, flexWrap: 'wrap' }}>
        <label>
          <div className="sehr-schwach" style={{ fontSize: 10 }}>Baureihe</div>

          <select
            value={z.baureihe ?? ''}
            disabled={laeuft}
            onChange={async (e) => {
              const r = reihen?.find((x) => x.baureihe === e.target.value);

              setLaeuft(true);
              try {
                // Die Grenzen der Baureihe mitsetzen: Sie begrenzen, was die
                // Bestückung erreichen kann — eine zweite Zahl neben MaxLinien.
                const a = await api.elektroZentraleSetzen(zentraleId, {
                  art: z.art,
                  baureihe: e.target.value || null,
                  hersteller: r?.hersteller ?? null,
                  maxRingeBaureihe: r?.maxRinge ?? null,
                  maxRingadressen: r?.maxRingadressen ?? null,
                  netzwerk: r?.netzwerk ?? null,
                });

                fertig(a ? a.message : 'Gesetzt.');
                await laden();
              } catch (err) {
                fertig(err instanceof Error ? err.message : String(err));
              } finally {
                setLaeuft(false);
              }
            }}
            style={{ fontSize: 12, minWidth: 200 }}
          >
            <option value="">— nicht gewählt —</option>
            {reihen?.filter((r) => r.art === z.art).map((r) => (
              <option key={r.id} value={r.baureihe}>
                {r.hersteller} {r.baureihe}
                {r.maxRinge ? ` — max ${r.maxRinge} Ringe` : ''}
              </option>
            ))}
          </select>
        </label>

        <div style={{ fontSize: 11 }}>
          <span className="sehr-schwach">Bestückung trägt</span>{' '}
          <strong>{daten.ausBestueckung}</strong>{' '}
          <span className="sehr-schwach">Ringe · Baureihe max</span>{' '}
          <strong>{z.maxRingeBaureihe ?? '—'}</strong>{' '}
          <span className="sehr-schwach">· gebildet</span>{' '}
          <strong>{daten.linien}</strong>{' '}
          <span className="sehr-schwach">
            · {daten.teilnehmer} Teilnehmer
            {z.maxRingadressen ? ` von ${z.maxRingadressen} Adressen` : ''}
          </span>
        </div>
      </div>

      {/* --- Die Befunde ---------------------------------------------- */}
      {daten.befunde.length > 0 ? (
        <div className="hinweis" style={{ marginBottom: 8 }}>
          <IconWarnung size={13} />
          <div style={{ fontSize: 11 }}>
            {daten.befunde.map((b, i) => (
              <div key={i} style={{ marginTop: i > 0 ? 4 : 0 }}>{b}</div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 11, marginBottom: 8 }}>
          <IconOk size={12} /> Die Bestückung trägt die gebildeten Linien.
        </div>
      )}

      {/* --- Die Baugruppen als Kachelgitter -------------------------- */}
      {daten.baugruppen.length > 0 && (
        <>
          <div className="baugruppen-gitter" style={{ marginBottom: 8 }}>
            {daten.baugruppen.map((g) => (
              <Baugruppenkachel
                key={g.id}
                g={g}
                laeuft={laeuft}
                entfernen={async () => {
                  setLaeuft(true);
                  try {
                    const a = await api.elektroBaugruppeEntfernen(g.id);
                    fertig(a ? a.message : 'Entfernt.');
                    await laden();
                  } catch (e) {
                    fertig(e instanceof Error ? e.message : String(e));
                  } finally {
                    setLaeuft(false);
                  }
                }}
              />
            ))}
          </div>

          {/* Die drei Farbwelten. Sie stehen einmal hier, weil sonst niemand
              weiß, was hellblau bedeutet. */}
          <div className="legende" style={{ margin: '4px 0 10px', fontSize: 10 }}>
            <span>
              <i style={{ width: 10, height: 10, borderRadius: 2, display: 'inline-block',
                          background: 'var(--modellfeld)',
                          border: '1px solid var(--modellfeld-rand)' }} />
              aus dem Revit-Modell gelesen
            </span>

            <span>
              <i style={{ width: 10, height: 10, borderRadius: 2, display: 'inline-block',
                          background: 'var(--datenfeld)',
                          border: '1px solid var(--datenfeld-rand)' }} />
              in der Plattform gepflegt
            </span>

            <span>
              <i style={{ width: 10, height: 10, borderRadius: 2, display: 'inline-block',
                          background: 'var(--baugruppe)',
                          border: '1px solid var(--baugruppe-rand)' }} />
              Bestückung des Geräts
            </span>

            <span>
              <i style={{ width: 10, height: 10, borderRadius: 2, display: 'inline-block',
                          background: 'var(--baugruppe)',
                          border: '1px dashed var(--warnung)' }} />
              Menge folgt aus einer Rechnung, die die Plattform nicht führt
            </span>
          </div>
        </>
      )}

      {/* --- Automatisch bilden --------------------------------------- */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                    marginBottom: 10, flexWrap: 'wrap' }}>
        <button
          onClick={() => { void bilden(false); }}
          disabled={laeuft}
          className="betont"
          style={{ fontSize: 12 }}
          title="Ein Ringmodul je Ring, ein SPKR-8 je acht Kreise, ein Taster je Etage — plus die festen Komponenten der Baureihe"
        >
          Bestückung automatisch bilden
        </button>

        <button
          onClick={() => { void bilden(true); }}
          disabled={laeuft}
          style={{ fontSize: 12 }}
          title="Zusätzlich die optionalen Komponenten — Lüftungstaster, Wind- und Regengeber"
        >
          mit optionalen
        </button>

        {/* Der Grund statt eines stummen gesperrten Knopfes.
            Ein Knopf, der nicht geht und nicht sagt warum, ist schlechter als
            einer, der eine Meldung liefert. */}
        {!z.baureihe && (
          <StatusBadge zustand="warnung" klein
                       text="Erst die Baureihe wählen — ohne sie sind die Komponenten unbekannt" />
        )}

        {daten.linien === 0 && (
          <StatusBadge zustand="warnung" klein
                       text="Keine Linie gebildet — ohne Linien ist die Bestückung nicht bemessbar" />
        )}

        <span className="sehr-schwach" style={{ fontSize: 10, flexBasis: '100%' }}>
          Komponenten, deren Menge aus einer <strong>Rechnung</strong> folgt —
          Verstärker, Netzteile, Akkus — werden mit Menge 1 und einem Vermerk
          angelegt. Die Verstärkerzahl folgt nicht aus der Kreiszahl, sondern aus der
          Summe der Lautsprecherleistungen und der Havarieleistung; sie automatisch
          zu setzen wäre eine Zahl, die wie ein Nachweis aussieht.
        </span>
      </div>

      {/* --- Eine Baugruppe hinzufügen -------------------------------- */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end',
                    flexWrap: 'wrap' }}>
        <label>
          <div className="sehr-schwach" style={{ fontSize: 10 }}>Art</div>
          <select value={neu.art}
                  onChange={(e) => setNeu({ ...neu, art: e.target.value })}
                  style={{ fontSize: 12 }}>
            {['ringmodul', 'netzwerkmodul', 'netzteil', 'akku', 'bedienfeld',
              'ansteuerung', 'gehaeuse', 'sonstiges'].map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>

        <label style={{ flex: 1, minWidth: 180 }}>
          <div className="sehr-schwach" style={{ fontSize: 10 }}>Bezeichnung</div>
          <input value={neu.bezeichnung}
                 onChange={(e) => setNeu({ ...neu, bezeichnung: e.target.value })}
                 placeholder="Ringmodul esserbus-PLus"
                 style={{ width: '100%', fontSize: 12 }} />
        </label>

        <label style={{ width: 60 }}>
          <div className="sehr-schwach" style={{ fontSize: 10 }}>Anzahl</div>
          <input value={neu.anzahl} inputMode="numeric"
                 onChange={(e) => setNeu({ ...neu, anzahl: e.target.value })}
                 style={{ width: '100%', fontSize: 12 }} />
        </label>

        {neu.art === 'ringmodul' && (
          <label style={{ width: 90 }}>
            <div className="sehr-schwach" style={{ fontSize: 10 }}>Ringe/Stk</div>
            <input value={neu.ringeJeStueck} inputMode="numeric"
                   onChange={(e) => setNeu({ ...neu,
                                             ringeJeStueck: e.target.value })}
                   style={{ width: '100%', fontSize: 12 }} />
          </label>
        )}

        <button
          onClick={async () => {
            if (!neu.bezeichnung.trim()) {
              fertig('Die Bezeichnung fehlt.');
              return;
            }

            setLaeuft(true);
            try {
              const a = await api.elektroBaugruppeAnlegen(zentraleId, {
                art: neu.art,
                bezeichnung: neu.bezeichnung.trim(),
                anzahl: Number(neu.anzahl) || 1,
                ringeJeStueck: neu.art === 'ringmodul'
                  ? Number(neu.ringeJeStueck) || 1 : null,
              });

              fertig(a ? a.message : 'Hinzugefügt.');
              setNeu({ ...neu, bezeichnung: '' });
              await laden();
            } catch (e) {
              fertig(e instanceof Error ? e.message : String(e));
            } finally {
              setLaeuft(false);
            }
          }}
          disabled={laeuft}
          className="betont"
          style={{ fontSize: 12 }}
        >
          hinzufügen
        </button>
      </div>

      <div className="sehr-schwach" style={{ fontSize: 10, marginTop: 6 }}>
        <strong>Anzahl × Ringe/Stk</strong> ergibt, wie viele Ringleitungen die
        Zentrale tragen kann. Das ist eine andere Zahl als das Maximum der Baureihe —
        und beide begrenzen: Eine Zentrale mit zwei Modulen führt keine achtzehn
        Ringe, und eine FX18 mit zwanzig Modulen auch nicht.
      </div>
    </div>
  );
}

// === Eine Baugruppenkachel ==================================================

/**
 * Eine Baugruppe als Kachel mit Sinnbild.
 *
 * --- Warum ein Gitter und keine Tabelle ------------------------------------------
 *
 * Eine Bestückung liest man als Bild, nicht Zeile für Zeile. Bei zwölf Baugruppen je
 * Zentrale ist die Frage „was steckt da" — und die beantwortet ein Gitter mit
 * Sinnbildern schneller als eine Tabelle mit zwölf gleich aussehenden Zeilen.
 *
 * --- Warum hellblau -------------------------------------------------------------
 *
 * Die Plattform kennzeichnet Felder nach Herkunft: hellgrün aus dem Revit-Modell,
 * hellgelb in der Plattform gepflegt. Eine Baugruppe ist keines von beiden — sie ist
 * eine Stückliste, eine Aussage über den Bestand eines Geräts.
 *
 * Sie in Grün oder Gelb zu setzen würde eine Herkunft behaupten, die sie nicht hat.
 *
 * --- Und der gestrichelte Rand --------------------------------------------------
 *
 * Er sagt: Diese Menge ist ein Merkposten, kein Ergebnis. Die Verstärkerzahl folgt
 * aus der Leistungsbemessung, nicht aus der Kreiszahl — eine gerechnete Zahl dort
 * wäre eine, die wie ein Nachweis aussieht.
 */
function Baugruppenkachel({
  g, laeuft, entfernen,
}: {
  g: {
    id: string; art: string; bezeichnung: string;
    hersteller: string | null; typ: string | null;
    artikelnummer: string | null; anzahl: number;
    ringeJeStueck: number | null;
    leistung: number | null; kapazitaet: number | null;
    hinweis: string | null;
  };
  laeuft: boolean;
  entfernen: () => Promise<void>;
}) {
  const [offen, setOffen] = useState(false);

  const ungerechnet = g.hinweis?.startsWith('MENGE NICHT GERECHNET') ?? false;

  // Das Sinnbild nach Art. Ein Ringmodul soll von einem Netzteil auf einen Blick
  // zu unterscheiden sein — bei zwölf Kacheln ist die Bezeichnung zu langsam.
  const zeichen = {
    ringmodul: <IconRingmodul size={15} />,
    netzwerkmodul: <IconNetzwerkmodul size={15} />,
    netzteil: <IconNetzteil size={15} />,
    akku: <IconAkku size={15} />,
    bedienfeld: <IconBedienfeld size={15} />,
    ansteuerung: <IconAnsteuerung size={15} />,
    gehaeuse: <IconGehaeuse size={15} />,
  }[g.art] ?? <IconBaugruppe size={15} />;

  // Die kennzeichnende Größe je Art. Bei einem Ringmodul die Ringe je Stück, bei
  // einem Netzteil die Leistung, bei einem Akku die Kapazität — dieselbe Kachel
  // trägt verschiedene Angaben, weil die Baugruppen verschieden sind.
  const groesse = g.ringeJeStueck != null
    ? `${g.ringeJeStueck} Ring${g.ringeJeStueck === 1 ? '' : 'e'} je Stück`
    : g.leistung != null ? `${g.leistung} W`
    : g.kapazitaet != null ? `${g.kapazitaet} Ah`
    : null;

  return (
    <div className={`baugruppe-kachel ${g.art}`
                    + (ungerechnet ? ' ungerechnet' : '')}>
      <div className="baugruppe-zeichen" title={g.art}>{zeichen}</div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
          <span className="baugruppe-menge" style={{ fontSize: 13 }}>
            {g.anzahl}×
          </span>

          <span style={{ fontSize: 11, lineHeight: 1.3 }}>{g.bezeichnung}</span>
        </div>

        <div className="sehr-schwach" style={{ fontSize: 10, marginTop: 2 }}>
          {[g.hersteller, g.typ, g.artikelnummer, groesse]
            .filter(Boolean).join(' · ') || g.art}
        </div>

        {ungerechnet && (
          <div style={{ fontSize: 10, marginTop: 3,
                        color: 'var(--warnung)' }}>
            Menge nicht gerechnet
          </div>
        )}

        {/* Der Hinweis erst auf Klick. Er ist bei manchen Baugruppen mehrere
            Sätze lang — die Steckplatzbilanz der FX18, die Reihenschaltung der
            Akkus —, und zwölf davon offen wären unlesbar. */}
        {g.hinweis && (
          <>
            <button
              onClick={() => setOffen(!offen)}
              style={{ fontSize: 9, padding: '0 4px', marginTop: 4 }}
            >
              {offen ? 'Hinweis schließen' : 'Hinweis'}
            </button>

            {offen && (
              <div style={{ fontSize: 10, marginTop: 4, lineHeight: 1.4 }}
                   className="schwach">
                {g.hinweis.replace(/^MENGE NICHT GERECHNET: /, '')}
              </div>
            )}
          </>
        )}
      </div>

      <button
        onClick={entfernen}
        disabled={laeuft}
        title="Diese Baugruppe entfernen"
        style={{ fontSize: 10, padding: '1px 5px', alignSelf: 'flex-start' }}
      >
        ×
      </button>
    </div>
  );
}
